import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { td } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus } from 'lucide-react';
import { fetchDrivers } from '@/services/driver';
export const Route = createFileRoute('/console/_authenticated/_layout/internal/_layout/drivers/')({
    component: DriversList,
});
const STATUS_VARIANT = {
    active: 'default',
    paused: 'secondary',
    inactive: 'outline',
};
function DriversList() {
    const { t } = useTranslation('internal');
    const { data: drivers = [], isLoading } = useQuery({ queryKey: ['drivers'], queryFn: fetchDrivers });
    return (<div className="flex flex-col gap-6 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-semibold">{t('drivers.title')}</h1>
                    <p className="text-sm text-muted-foreground">{t('drivers.subtitle')}</p>
                </div>
                <Button asChild>
                    <Link to="/console/internal/drivers/new">
                        <Plus className="h-4 w-4"/>
                        {t('drivers.new')}
                    </Link>
                </Button>
            </div>

            {isLoading ? (<p className="text-sm text-muted-foreground">{t('common.loading')}</p>) : drivers.length === 0 ? (<Card>
                    <CardHeader>
                        <CardTitle>{t('drivers.empty_title')}</CardTitle>
                        <CardDescription>{t('drivers.empty_desc')}</CardDescription>
                    </CardHeader>
                </Card>) : (<div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {drivers.map((driver) => (<Link key={driver.publicId} to="/console/internal/drivers/$publicId" params={{ publicId: driver.publicId }} className="block">
                            <Card className="h-full hover:border-primary transition-colors">
                                <CardContent className="pt-6 space-y-3">
                                    <div className="flex items-start gap-3">
                                        {driver.vehicleImageUrl ? (<img src={driver.vehicleImageUrl} alt={`${driver.fullName} vehicle`} className="h-16 w-16 rounded object-cover border shrink-0" loading="lazy"/>) : (<div className="h-16 w-16 rounded border bg-muted text-xs flex items-center justify-center text-muted-foreground shrink-0">
                                                {td(t, `enum.vehicle_type.${driver.vehicleType}`).slice(0, 3).toUpperCase()}
                                            </div>)}
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="font-semibold truncate">{driver.fullName}</p>
                                                    <p className="text-xs text-muted-foreground truncate">
                                                        {driver.phoneNumber}
                                                    </p>
                                                </div>
                                                <Badge variant={STATUS_VARIANT[driver.status]} className="shrink-0">
                                                    {td(t, `enum.driver_status.${driver.status}`)}
                                                </Badge>
                                            </div>
                                            <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                                                <p className="truncate">
                                                    {td(t, `enum.vehicle_type.${driver.vehicleType}`)} ·{' '}
                                                    {driver.licensePlate}
                                                </p>
                                                <p className="truncate">
                                                    {t('drivers.payout_method_label', {
                    method: td(t, `enum.payout_method.${driver.payoutMethod}`),
                })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </Link>))}
                </div>)}
        </div>);
}
